import React, {
  useRef,
} from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  ServiceFacilityDTO,
} from 'dtos';
import Tooltip from 'core/components/Tooltip';
import compact from 'lodash/compact';
import UsesCell from 'views/EMR/Settings/BillingConfigurations/UsesCell';
import pick from 'lodash/pick';
import {
  DeleteConfigCell,
  NPICell,
} from 'views/EMR/Settings/BillingConfigurations/columns';

const usesKeys = ['appointments', 'claims', 'availibilitySlots', 'billingProviderRules'];

type TruncatedTooltipCellProps = {
  title?: string | null;
};

function TruncatedTooltipCell({ title }: TruncatedTooltipCellProps) {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <Tooltip title={title} auto ref={ref}>
      <div className="truncate w-full" ref={ref}>
        {title ?? ''}
      </div>
    </Tooltip>
  );
}

const serviceFacilityColumns: ColumnDef<ServiceFacilityDTO>[] = [{
  id: 'nickname',
  accessorKey: 'nickname',
  columnTitle: 'Nickname',
  size: 120,
  cell: ({ row: { original: { nickname } } }) => <TruncatedTooltipCell title={nickname} />,
}, {
  id: 'officialName',
  accessorKey: 'officialName',
  columnTitle: 'Official Name',
  size: 220,
  cell: ({ row: { original: { officialName } } }) => <TruncatedTooltipCell title={officialName} />,
}, {
  id: 'address',
  accessorKey: 'address',
  columnTitle: 'Address',
  meta: { autoSized: true },
  cell: ({ row: { original: { address } } }) => (
    <div className="flex gap-1 w-full">
      <TruncatedTooltipCell title={address?.formattedAddresses?.oneLine ?? '--'} />
    </div>
  ),
}, {
  id: 'npi',
  accessorKey: 'npi',
  columnTitle: 'NPI',
  size: 350,
  cell: ({ row: { original: { npi } } }) => (<NPICell npi={npi} />),
}, {
  id: 'defaultPOS',
  accessorKey: 'defaultPOS',
  columnTitle: 'POS',
  size: 48,
  cell: ({ row: { original: { defaultPOS } } }) => (
    <div>
      {defaultPOS}
    </div>
  ),
}, {
  id: 'properties',
  accessorKey: 'isInternal',
  columnTitle: 'Properties',
  size: 200,
  cell: ({ row: { original: { isInternal, isPrincipal, isInboundSchedulingEnabled } } }) => {
    const properties = compact([
      isPrincipal && 'Principal',
      isInternal && 'Internal',
      isInboundSchedulingEnabled && 'Inbound Scheduling']).join(', ');
    return (
      <div className="flex gap-1">
        {properties.length > 0 ? (
          <div>
            {properties}
          </div>
        ) : '—'}
      </div>
    );
  },
}, {
  id: 'stats',
  accessorKey: 'stats',
  columnTitle: 'Uses',
  size: 96,
  minSize: 48,
  cell: ({ row: { original: { stats, serviceFacilityId } } }) => (
    <UsesCell
      {...pick(stats, usesKeys) as Record<string, number>}
      id={serviceFacilityId ?? 0}
    />
  ),
}, {
  id: 'actions',
  accessorKey: 'actions',
  columnTitle: '',
  size: 48,
  cell: ({ row: { original: { serviceFacilityId, stats } } }) => (
    <DeleteConfigCell
      type="serviceFacility"
      configTitle="Service Facility"
      payload={{ serviceFacilityId }}
      canDelete={stats?.canDelete ?? false}
      tooltip={!stats?.canDelete ? 'Cannot delete a facility that is in use or marked as principal' : undefined}
    />
  ),
}];

export default serviceFacilityColumns;
